/* eslint-disable no-alert */
import {
  useEffect, useState, useLayoutEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import Loader from '../../juristec-ui/core/Loader';

import firebase from '../../utils/firebase';
import {
  getRefUser, getRefCompany, getRefOnRegister, getRefSession, getRefCompanySecs, getPlans,
  getRefUsedTokens, getRefBonusTokens,
  getRefSupportPermissions,
} from '../../utils/firestore';
import useSessionStorage from '../../hooks/useSessionStorage';

import authContextCreated from './provider'; // para n estragar os imports no resto do metrics...

import getRequestMeta, { getPublicRequestMeta } from '../../utils/functions/generateMeta';
import { error as errorLabels } from '../../label';
import { ip } from '../../utils/functions/urls';

import { supportEmail } from '../../options';

export const AuthContext = authContextCreated;

export const AuthProvider = ({ children, hydrate = null }) => {
  const sessionListener = useRef();
  const tokenListener = useRef();
  const bonusListener = useRef();
  const [pending, setPending] = useState(true);
  const [msgError, setMsgError] = useState(undefined);
  const [currentUser, setCurrentUser] = useState(null);
  const [plans, setPlans] = useState(null);
  const [gptUsedTokens, setGptUsedTokens] = useState(0);
  const [gptBonusTokens, setGptBonusTokens] = useState(0);
  const [user, setUser] = useSessionStorage('user', null);
  const [claimsUser, setClaimsUser] = useSessionStorage('claims', null);
  const [userCompany, setCompanyUser] = useSessionStorage('company', null);
  const [session,, getSession] = useSessionStorage('session', null);

  function clearSessionStorageAuth() {
    setCompanyUser(null);
    setClaimsUser(null);
    setUser(null);
    setMsgError(undefined);
  }

  function setCompanyPhotoUrl(photoUrl) {
    setCompanyUser({ ...userCompany, photoUrl });
  }

  function changeUserConfigs(configs) {
    setUser({ ...user, configs: { ...user.configs, ...configs } });
  }

  function changeUserAttrs(attrs) {
    setUser({ ...user, ...attrs });
  }

  /**
   * Verifica se o usuário possui permissão para determinadas
   * operações em relação ao plano contratado.
   * @param {string} field - O nome da operação que deseja verificar.
   * @param {number} [actualQty] - Quantidade de items atual, caso a operação tenha limite.
   * @returns {boolean} - Retorna `true` se possui permissão, `false` caso contrário.
   * Se `actualQty` for fornecido, retorna true se o campo possui
   * menos do que a quantidade fornecida.
   * Se nenhum valor for fornecido, retorna true se o campo possui
   * um valor verdadeiro ou um valor de plano válido.
   */
  function checkPlanPermission(field, actualQty = null) {
    const fieldValue = userCompany[field] ?? plans[userCompany.plan][field] ?? true;
    return actualQty !== null ? actualQty < fieldValue : fieldValue;
  }

  async function getLoginToken(email, password, gToken) {
    try {
      const msg = {
        ...getPublicRequestMeta(gToken, 'GET', '', `${email}:${password}`),
      };
      const res = await fetch(`${ip}/auth/login`, msg);

      if (res.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      const json = await res.json();
      if (res.status !== 200) {
        return { error: true, msg: errorLabels.fetchGeneric, raw: json.error };
      }

      return { error: false, msg: null, res: json };
    } catch (er) {
      // console.log(er);
      return { error: true, msg: errorLabels.fetchGeneric, raw: `Erro do sistema: ${er.toString()}` };
    }
  }

  async function checkRecaptchaToken(gToken) {
    try {
      const msg = {
        ...getPublicRequestMeta(gToken, 'GET', ''),
      };
      const res = await fetch(`${ip}/auth/captcha`, msg);

      if (res.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      const json = await res.json();
      if (res.status !== 200) {
        if (res.status === 403) {
          return { error: true, msg: errorLabels.fetchGeneric, raw: 'forbidden' };
        }
        return { error: true, msg: errorLabels.fetchGeneric, raw: json.error };
      }

      return { error: false, msg: null, res: json };
    } catch (er) {
      // console.log(er);
      return { error: true, msg: errorLabels.fetchGeneric, raw: `Erro do sistema: ${er.toString()}` };
    }
  }

  async function getLoginProviderByEmail(email, gToken) {
    try {
      const msg = {
        ...getPublicRequestMeta(gToken, 'GET', ''),
      };
      const res = await fetch(`${ip}/auth/provider/${email}`, msg);

      if (res.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      const json = await res.json();
      if (res.status !== 200) {
        return { error: true, msg: errorLabels.fetchGeneric, raw: json.error };
      }
      return { error: false, msg: null, providerId: json.info };
    } catch (er) {
      // console.log(er);
      return { error: true, msg: errorLabels.fetchGeneric, raw: `Erro do sistema: ${er.toString()}` };
    }
  }

  async function createUserFromLoginProvider(loggedUser) {
    try {
      const token = await loggedUser.getIdToken();
      const opt = await getRequestMeta(token, 'POST', 'JSON');
      const res = await fetch(`${ip}/auth/provider/new_user`, opt);

      if (res.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      const json = await res.json();
      if (res.status !== 200) {
        return { error: true, msg: errorLabels.fetchGeneric, raw: json.error };
      }
      return { error: false, msg: null, res: json.info };
    } catch (er) {
      // console.log(er);
      return { error: true, msg: errorLabels.fetchGeneric, raw: `Erro do sistema: ${er.toString()}` };
    }
  }

  useLayoutEffect(() => {
    if (currentUser) setPending(false);
    else {
      setPending(true);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!pending && session?.length > 0 && currentUser && !hydrate) {
      const checkUserSession = async (doc) => {
        const sessionDoc = await doc.data();
        if (!user.multiSessions && sessionDoc?.session !== session) {
          firebase.logout('Multisession');
          alert(`
            A sessão atual será encerrada, pois uma nova sessão foi iniciada com sua conta.
            Se você não iniciou essa nova sessão, entre em contato com o suporte: ${supportEmail()}.
          `);
        }
      };
      sessionListener.current = firebase.snapShotListener(
        getRefSession(currentUser.uid), checkUserSession,
      );
      const checkUsedTokens = async (doc) => {
        if (doc.exists()) {
          const tkDoc = await doc.data();
          setGptUsedTokens(tkDoc.used);
        }
      };
      const checkBonusTokens = async (doc) => {
        if (doc.exists()) {
          const bonusDoc = await doc.data();
          setGptBonusTokens(bonusDoc.available);
        }
      };
      tokenListener.current = firebase.snapShotListener(
        getRefUsedTokens(
          user.company,
          new Date().toISOString().replace(/-\d+T.*/, ''),
        ),
        checkUsedTokens,
      );
      bonusListener.current = firebase.snapShotListener(
        getRefBonusTokens(user.company),
        checkBonusTokens,
      );
    }
    return () => {
      if (sessionListener?.current) sessionListener.current();
      if (tokenListener?.current) tokenListener.current();
      if (bonusListener?.current) bonusListener.current();
    };
  }, [
    pending,
    currentUser,
    sessionListener,
    tokenListener,
    bonusListener,
    user,
    session,
    hydrate,
  ]);

  async function getUserInfo(uid) {
    try {
      const refUser = getRefUser(uid);
      const elen = await refUser.get();

      if (!elen || !elen.exists) {
        firebase.logout('getUserInfo404');
        clearSessionStorageAuth();
        return;
      }

      const userData = elen.data();
      setUser({ ...userData, id: elen.id });

      if ('company' in userData && !userCompany) {
        const [
          companySnap,
          secsSnap,
          plansSnap,
          supportPermissionsSnap,
        ] = await Promise.all([
          getRefCompany(userData.company).get(),
          getRefCompanySecs(userData.company).get(),
          getPlans().get(),
          getRefSupportPermissions(userData.company)
            .orderBy('validUntil', 'desc')
            .limit(1)
            .get(),
        ]);

        const userCompanyInfo = {
          ...companySnap.data(),
          isAdmin: userData?.isAdmin || false,
          id: companySnap.id,
        };

        const secs = secsSnap.docs.map((doc) => doc.id);

        setPlans(plansSnap.data() ?? null);

        let supportPermissionReports = false;
        if (!supportPermissionsSnap.empty) {
          const doc = supportPermissionsSnap.docs[0];
          supportPermissionReports = doc.data().validUntil.toDate() > new Date();
        }

        // único setState
        setCompanyUser({
          ...userCompanyInfo,
          apiGedCredentials: secs.includes('l1reports'),
          supportPermissionReports,
        });
      } else {
        setCompanyUser({ isAdmin: userData?.isAdmin || false });
      }
    } catch (error) {
      firebase.logout('getUserInfoError');
      clearSessionStorageAuth();
    }
  }

  async function setupUserSession(authUser) {
    if (authUser) {
      if (!claimsUser) {
        authUser.getIdTokenResult().then((result) => {
          const companyId = result?.claims?.companyId || null;
          if (!userCompany && companyId) {
            getRefCompany(companyId)
              .get()
              .catch(() => {
                firebase.logout('setupUserSessionCompany');
              })
              .then((companyResp) => {
                setCompanyUser(companyResp.data());
              })
              .catch(() => {
                firebase.logout('setupUserSessionCompanyData');
              });
          }
          const claims = result?.claims || {};
          setClaimsUser(claims);
        });
      }
      await getUserInfo(authUser.uid);
      // Acessando firebase
      if (!user) {
        await getUserInfo(authUser.uid);
      }
    }
    getSession();
    setCurrentUser(authUser);
    setPending(false);
  }

  useEffect(() => {
    if (hydrate) {
      // compatibilidade com modalApi
      setCurrentUser(hydrate.currentUser);
      setClaimsUser(hydrate.claimsUser);
      setCompanyUser(hydrate.userCompany);
      setUser(hydrate.user);
      setPlans(hydrate.plans);
      setGptUsedTokens(hydrate.gptUsedTokens);
      setGptBonusTokens(hydrate.gptBonusTokens);
      setPending(false);
      return;
    }

    firebase.authStateObserver(async (authUser) => {
      if (authUser) {
        if (!authUser.emailVerified) {
          firebase.auth.currentUser.sendEmailVerification();
          setMsgError('Email não verificado');
          // alert('Verifique sua caixa de email para confirmar seu cadastro!');
          clearSessionStorageAuth();
          firebase.logout('emailNotVerified');
          return;
        }

        const { email, uid } = firebase.auth.currentUser;
        const userOk = await getRefUser(uid).get();
        if (!userOk.exists()) {
          const newUser = await getRefOnRegister(email).get();
          if (newUser.exists()) {
            const userInfo = await newUser.data();
            // await firebase.db.doc(`onregister/${email}`).delete();
            // a responsabilidade de deletar está com o backend
            const token = await firebase.auth.currentUser.getIdToken();
            const msg = {
              ...(await getRequestMeta(token, 'POST', 'JSON')),
              body: JSON.stringify({
                plan: userInfo.plan,
              }),
            };
            const res = await fetch(`${ip}/manage/company`, msg);
            const json = await res.json();
            getRefUser(uid).update({ lastLogin: new Date(authUser.metadata.lastSignInTime) });
            console.log(json);
          } else {
            clearSessionStorageAuth();
            firebase.logout('user404');
            return;
          }
        }
        // undefined = 3600000
        // 0 = Infinity
        const tOut = userOk.data().sessionTimeout ?? 60 * 60 * 1000;
        const date = new Date();
        const time = window.localStorage.getItem('lastActionTime');
        if (time && (date.getTime() - time) >= (tOut || Infinity)) {
          clearSessionStorageAuth();
          firebase.logout('timeout');
        }

        try {
          await getRefOnRegister(email).delete();
        } catch (error) {
          console.log(error);
        }
        // await firebase.db.doc(`onregister/${email}`).get()
        //   .then(async (snapshot) => {
        //     if (snapshot.exists()) {
        //       await firebase.db.doc(`onregister/${email}`).delete()
        //     }
        //   });
        // Acessando claims
        // window.pendo.initialize({
        //   visitor: {
        //     id: authUser.uid, // Required if user is logged in
        //     email: authUser.email, // Recommended if using Pendo Feedback, or NPS Email
        //     // full_name:    // Recommended if using Pendo Feedback
        //     // role:         // Optional
        //     // You can add any additional visitor level key-values here,
        //     // as long as it's not one of the above reserved names.
        //   },
        //   account: {
        //     id: authUser.uid, // Highly recommended
        //     email: authUser.email,
        //     // name:         // Optional
        //     // is_paying:    // Recommended if using Pendo Feedback
        //     // monthly_value:// Recommended if using Pendo Feedback
        //     // planLevel:    // Optional
        //     // planPrice:    // Optional
        //     // creationDate: // Optional
        //     // You can add any additional account level key-values here,
        //     // as long as it's not one of the above reserved names.
        //   },
        // });
        // if (!claimsUser) {
        //   authUser.getIdTokenResult().then((result) => {
        //     const companyId = result?.claims?.companyId || null;
        //     if (!userCompany && companyId) {
        //       getRefCompany(companyId)
        //         .get()
        //         .catch(() => {
        //           firebase.logout();
        //         })
        //         .then((companyResp) => {
        //           setCompanyUser(companyResp.data());
        //         })
        //         .catch(() => {
        //           firebase.logout();
        //         });
        //     }
        //     const claims = result?.claims || {};
        //     setClaimsUser(claims);
        //   });
        // }
        // Acessando firebase
        // if (!user) {
        //   await getUserInfo(authUser.uid);
        // }
      } else if (user || claimsUser || userCompany) {
        clearSessionStorageAuth();
      }
      await setupUserSession(authUser);
    });
  }, []);

  return pending ? (
    !hydrate && <Loader debug="authProvider" />
  ) : (
    <AuthContext.Provider
      value={{
        currentUser,
        claimsUser,
        userCompany,
        user,
        plans,
        gptUsedTokens,
        gptBonusTokens,
        msgError,
        setCompanyPhotoUrl,
        changeUserConfigs,
        changeUserAttrs,
        getLoginToken,
        checkRecaptchaToken,
        getLoginProviderByEmail,
        createUserFromLoginProvider,
        setupUserSession,
        checkPlanPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  hydrate: PropTypes.shape({
    currentUser: PropTypes.shape({}),
    claimsUser: PropTypes.shape({}),
    userCompany: PropTypes.shape({}),
    user: PropTypes.shape({}),
    plans: PropTypes.shape({}),
    gptUsedToken: PropTypes.number,
  }),
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.func,
  ]).isRequired,
};
